import React, { memo, useEffect, useMemo } from 'react';
import { Grid, List, ListItem } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { cloneDeep, set, uniqBy } from 'lodash';
import { useHistory } from 'react-router';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { object as YupObject, string as YupString, array as YupArray } from 'yup';
import { DateTime } from 'luxon';
import { encodeDate, unpackToDate } from '@campfire/hot-date';
import { volunteerProfileHardCodedFields } from '../../../../../../common/form/task-form/model/get-volunteer-profile-hardcoded-field-values';
import { refereeCheckHardCodedFields } from '../../../../../../common/form/task-form/model/get-referee-check-hardcoded-field-values';
import { Recurrence } from '../../../../../../common/recurrence/__generated__/Recurrence';
import { assertNever } from '../../../../../../common/functions/assert-never';
import { arrayHead } from '../../../../../../common/functions/array-head';

import { useCampfireLazyQuery } from '../../../../../../global/network/useCampfireLazyQuery';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { useAdminConsoleActions } from '../../../admin-console-actions';
import {
  CakeSelectItems,
  DraggableTaskItemContent,
  TaskFormType,
  ExpiryRecurrence,
  TaskStageSelectItems,
  TaskStage,
} from '../admin-console-volunteer-profile-model';
import {
  ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK,
  ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_PROGRAMS,
  ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_ROLES,
} from '../admin-console-volunteer-profile.gql';
import { useLoadingContext } from '../AdminConsoleLoadingContext';
import { AdminConsoleVolunteerProfileHeader } from '../AdminConsoleVolunteerProfileHeader';
import {
  AdminConsoleGetSingleVolunteerProfileTask,
  AdminConsoleGetSingleVolunteerProfileTaskVariables,
} from '../__generated__/AdminConsoleGetSingleVolunteerProfileTask';
import { AdminConsoleGetVolunteerProfileTasks_vm_cakes as CakeData } from '../__generated__/AdminConsoleGetVolunteerProfileTasks';
import { AdminConsoleVolunteerProfileTaskBuilderGetAllPrograms } from '../__generated__/AdminConsoleVolunteerProfileTaskBuilderGetAllPrograms';
import { Task_taskItems as TaskItems } from '../__generated__/Task';
import { FormBuilderDialogProvider } from './FormBuilderDialogContext';
import { FormBuilderHeader } from './FormBuilderHeader';
import { TaskControlsCard, TaskControlsContent } from './TaskControlsCard';
import { TaskDetailsCard } from './task-details-card/TaskDetailsCard';
import { AdminConsoleVolunteerProfileTaskBuilderGetAllRoles } from '../__generated__/AdminConsoleVolunteerProfileTaskBuilderGetAllRoles';
import { deserializeTaskItems, TaskItemsDeserialize } from './utils';
import { HardCodedFieldTaskItem } from './task-list/task-items/task-item-fields/HardCodedFieldTaskItem';
import { TaskListItemsForm } from './task-list/TaskListItemsForm';
import { GET_TASK_STAGES } from '../../../../tasks/TasksList/task-stages.gql';
import { GetTaskStages } from '../../../../tasks/TasksList/__generated__/GetTaskStages';

const createTaskItem = (taskItemId: string, values: TaskItemsDeserialize, isDuplicate?: boolean) => {
  const taskItem = cloneDeep(values.byId[taskItemId]);

  const shouldClearId = (id: string) => isDuplicate || id.startsWith('new') || id.includes('new');

  if (shouldClearId(taskItem.taskItemId)) {
    set(taskItem, 'taskItemId', undefined);
  }

  if (taskItem.__typename === 'VOLUNTEER_TaskItemContentType') {
    set(
      taskItem,
      'attachments',
      taskItem.attachments.map((attachment) => ({
        ...attachment,
        attachmentId: shouldClearId(attachment.attachmentId) ? undefined : attachment.attachmentId,
      }))
    );
  }

  if (taskItem.__typename !== 'VOLUNTEER_TaskItemFieldType') {
    return taskItem;
  }

  if (isDuplicate) {
    set(taskItem, 'field.taskFieldId', undefined);
  }

  if (taskItem.field.__typename === 'VOLUNTEER_MultipleChoiceTaskFieldType') {
    set(
      taskItem,
      'field.multipleChoiceOptions',
      taskItem.field.multipleChoiceOptions.map((option) => ({
        ...option,
        multipleChoiceFieldOptionId: shouldClearId(option.multipleChoiceFieldOptionId)
          ? undefined
          : option.multipleChoiceFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.multipleChoiceFieldOptionId]?.triggerItemIds || []).map(
            (itemId, itemIndex) => ({
              item: createTaskItem(itemId, values, isDuplicate),
              order: itemIndex,
            })
          ),
        },
      }))
    );
  }

  if (taskItem.field.__typename === 'VOLUNTEER_CheckboxTaskFieldType') {
    set(
      taskItem,
      'field.checkboxOptions',
      taskItem.field.checkboxOptions.map((option) => ({
        ...option,
        checkboxFieldOptionId: shouldClearId(option.checkboxFieldOptionId) ? undefined : option.checkboxFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.checkboxFieldOptionId]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createTaskItem(itemId, values, isDuplicate),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  if (taskItem.field.__typename === 'VOLUNTEER_DropdownTaskFieldType') {
    set(
      taskItem,
      'field.dropdownOptions',
      taskItem.field.dropdownOptions.map((option) => ({
        ...option,
        dropdownFieldOptionId: shouldClearId(option.dropdownFieldOptionId) ? undefined : option.dropdownFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.dropdownFieldOptionId]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createTaskItem(itemId, values, isDuplicate),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  if (taskItem.field.__typename === 'VOLUNTEER_QuizTaskFieldType') {
    const correctId = taskItem.field.quizTaskFieldCorrect?.quizTaskFieldOption?.quizTaskFieldOptionId;
    set(
      taskItem,
      'field.quizTaskFieldOptions',
      taskItem.field.quizTaskFieldOptions.map((option) => ({
        ...option,
        isCorrect: option.quizTaskFieldOptionId === correctId,
        quizTaskFieldOptionId: shouldClearId(option.quizTaskFieldOptionId) ? undefined : option.quizTaskFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.quizTaskFieldOptionId]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createTaskItem(itemId, values, isDuplicate),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  if (taskItem.field.__typename === 'VOLUNTEER_CrossTaskDynamicTaskFieldType') {
    set(
      taskItem,
      'field.crossTaskDynamicOptions',
      taskItem.field.crossTaskDynamicOptions.map((option) => ({
        ...option,
        crossTaskDynamicFieldOptionId: shouldClearId(option.crossTaskDynamicFieldOptionId ?? '')
          ? undefined
          : option.crossTaskDynamicFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.sourceOptionValue]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createTaskItem(itemId, values, isDuplicate),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  return taskItem;
};

const validationSchema = YupObject().shape({
  title: YupString().required('Task name is required'),
  cakeId: YupString().required('Please select a cake'),
  taskStageId: YupString().nullable(),
  programIds: YupArray().required('Please select a program'),
});

export type LocationState = {
  state: {
    tab?: string;
  };
};
type ExtendedTaskItems = TaskItems | DraggableTaskItemContent;
interface Props {
  cakeSelectItems: CakeSelectItems;
  remainingManualTasks: number;
  refetch?: () => Promise<{ data: any }>;
  cakesData?: CakeData[];
}

export const TaskFormBuilder = memo((props: Props) => {
  const { cakeSelectItems, refetch, remainingManualTasks, cakesData } = props;
  const { theme, isMobile } = useCampfireTheme();

  const [query] = useQueryParams({
    tid: StringParam,
    cid: StringParam,
    duplicate: NumberParam,
    type: StringParam,
  });

  const { tid, duplicate, cid, type } = query;
  const { runSaveTask, runSaveTaskIsLoading } = useAdminConsoleActions();
  const { setIsLoading } = useLoadingContext();
  const history = useHistory();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const saveTaskStageMutation = useCampfireFetch({ defer: true });

  // Calculate the next order for new tasks in the selected cake
  const getNextOrderForCake = (cakeId: string): number => {
    if (!cakesData) return 0;

    const cake = cakesData.find((c) => c.cakeId === cakeId);
    if (!cake || !cake.allTasks.length) return 0;

    // Filter out the "Volunteer Profile" task (order = -1) and get the highest order
    const nonProfileTasks = cake.allTasks.filter((task) => task.order !== -1);
    if (!nonProfileTasks.length) return 0;

    const maxOrder = Math.max(...nonProfileTasks.map((task) => task.order));
    return maxOrder + 1;
  };

  const [getTask, { data, loading }] = useCampfireLazyQuery<
    AdminConsoleGetSingleVolunteerProfileTask,
    AdminConsoleGetSingleVolunteerProfileTaskVariables
  >(ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK);

  const { data: getAllProgramsData, loading: getAllProgramsLoading } = useCampfireQuery<
    AdminConsoleVolunteerProfileTaskBuilderGetAllPrograms,
    undefined
  >(ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_PROGRAMS);

  const { data: getAllRolesData, loading: getAllRolesLoading } = useCampfireQuery<
    AdminConsoleVolunteerProfileTaskBuilderGetAllRoles,
    undefined
  >(ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_ROLES);

  const { data: taskStagesData, loading: taskStagesLoading, refetch: refetchTaskStages } = useCampfireQuery<
    GetTaskStages,
    {}
  >(GET_TASK_STAGES);

  const taskStageSelectItems: TaskStageSelectItems = useMemo(() => {
    const stages = taskStagesData?.vm.taskStages || [];
    return stages
      .sort((a, b) => a.order - b.order)
      .map((stage) => ({
        label: `Stage ${stage.order}: ${stage.name}`,
        value: stage.taskStageId,
      }));
  }, [taskStagesData?.vm.taskStages]);

  const handleTaskStageAdd = async (taskStage: Omit<TaskStage, 'taskStageId'>) => {
    // Call the SaveTaskStage API endpoint
    try {
      const res = await saveTaskStageMutation.run({
        url: `${apiUrl}/vm/task/stage/save`,
        method: 'post',
        data: {
          name: taskStage.name,
          automateApproval: taskStage.automateApproval,
          order: taskStage.order,
        },
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Unable to save task stage',
          variant: 'error',
        });
        throw new Error('Failed to save');
      }

      setSnackbar({
        open: true,
        message: 'Task stage created successfully',
        variant: 'success',
      });

      // Refetch task stages to update the dropdown and get the new stage ID
      const refetchResult = await refetchTaskStages();

      // Find the newly created task stage by matching name and order
      const newStage = refetchResult.data?.vm.taskStages?.find(
        (stage: any) => stage.name === taskStage.name && stage.order === taskStage.order
      );

      // Return the new stage ID so it can be selected
      return newStage?.taskStageId;
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Unable to save task stage',
        variant: 'error',
      });
      throw error;
    }
  };

  const permissionLevelOptions = [
    { label: 'All Permission Levels', value: 'none' },
    { label: 'General Volunteer', value: 'gv' },
    { label: 'Activity Leader', value: 'al' },
    { label: 'Program Manager', value: 'pm' },
    { label: 'Admin', value: 'admin' },
  ];

  const programsOfTask = data?.vm.task?.programs ?? [];
  const activitiessOfTask = data?.vm.task?.activities ?? [];
  const rolesOfTask = data?.vm.task?.roles ?? [];

  const programOptions = useMemo(() => {
    const activePrograms = getAllProgramsData?.vm.programs.filter((program) => program.dateSuspended === null) ?? [];
    return uniqBy([...activePrograms, ...programsOfTask], 'programId').sort((a, b) => a.name.localeCompare(b.name));
  }, [getAllProgramsData, programsOfTask]);

  const roleOptions = useMemo(() => {
    const allRoles = getAllRolesData?.vm.activityRoles ?? [];
    return uniqBy([...allRoles, ...rolesOfTask], 'activityRoleId').sort((a, b) => a.name.localeCompare(b.name));
  }, [getAllProgramsData, rolesOfTask]);

  useEffect(() => {
    if (tid)
      getTask({
        variables: {
          taskId: tid,
        },
      });
  }, [tid]);

  const isLoading = loading || runSaveTaskIsLoading || getAllProgramsLoading || getAllRolesLoading || taskStagesLoading;

  useEffect(() => setIsLoading(isLoading), [isLoading]);

  const getFrequency = (dataGetTask: AdminConsoleGetSingleVolunteerProfileTask): ExpiryRecurrence | undefined => {
    const task = dataGetTask?.vm.task;
    if (!task) return undefined;

    const recurrence = arrayHead(task.expiryRecurrence?.recurrences || []);
    if (!recurrence) return undefined;

    if (recurrence.__typename === 'MonthlyByMonthDayRecurrenceType') {
      switch (recurrence.monthInterval) {
        case 1:
          return 'monthly';
        case 3:
          return 'quarterly';
        case 6:
          return 'half_annually';
        case 12:
          return 'annually';
        default:
          break;
      }
    }
    return undefined;
  };
  const getStartDateRecurrence = (dataGetTask: AdminConsoleGetSingleVolunteerProfileTask): Date | undefined => {
    const task = dataGetTask?.vm.task;
    if (!task) return undefined;
    const recurrence = arrayHead(task.expiryRecurrence?.recurrences || []);
    if (!recurrence) return undefined;
    return unpackToDate(recurrence.startDate).toJSDate();
  };

  const initValues: TaskFormType = useMemo(() => {
    // For event and referee check tasks, force onboarding cake
    let selectedCakeId = cid ?? data?.vm.task?.cake.cakeId ?? cakeSelectItems[0]?.value ?? '';

    if (type === 'event' || type === 'referee_check' || type === 'manager' || type === 'stripe_product') {
      // Find the onboarding cake ID from available options
      const onboardingCake = cakeSelectItems.find(
        (cake) => cake.label.toLowerCase().includes('onboarding') || cake.label.toLowerCase().includes('onboard')
      );
      if (onboardingCake) {
        selectedCakeId = onboardingCake.value;
      }
    }

    const isNewTask = !tid || duplicate;

    return {
      taskId: !duplicate ? tid ?? undefined : undefined,
      title: `${tid && duplicate ? 'Duplicate: ' : ''}${data?.vm.task?.title ?? 'New Task'}`,
      description: data?.vm.task?.description ?? '',
      cakeId: selectedCakeId,
      taskStageId: data?.vm.task?.taskStage?.taskStageId ?? null,
      order: isNewTask ? getNextOrderForCake(selectedCakeId) : data?.vm.task?.order ?? 0,
      allowUpdate: data?.vm.task?.allowUpdate ?? false,
      isHidden: data?.vm.task?.isHidden ?? false,
      applicantsOnly: data?.vm.task?.applicantsOnly ?? (type === 'event' || type === 'referee_check' || false),
      adminOnly: data?.vm.task?.adminOnly ?? false,
      managerCompleted: data?.vm.task?.managerCompleted ?? (type === 'manager' || false),
      blocksProgress: data?.vm.task?.blocksProgress ?? false,
      taskItems: deserializeTaskItems(data?.vm.task?.taskItems),
      automateApproval: data?.vm.task?.automateApproval ?? type === 'manager',
      // Note: For event (type === 'event') and referee check (type === 'referee_check') tasks,
      // the backend will determine the task type based on the task configuration and items
      frequency: data ? getFrequency(data) : undefined,
      startDate: data ? getStartDateRecurrence(data) : undefined,
      expiryFromCompletion: data?.vm.task?.expiryFromCompletion ?? false,
      roleIds: !roleOptions.length
        ? []
        : rolesOfTask.length > 0
        ? rolesOfTask.flatMap((role) => role.activityRoleId)
        : [],
      programIds: !programOptions.length
        ? []
        : programsOfTask.length > 0
        ? programsOfTask.flatMap((program) => program.programId)
        : ['all'],
      activityIds: activitiessOfTask.length > 0 ? activitiessOfTask.flatMap((activity) => activity.activityId) : [],
      permissionLevel: data?.vm.task?.permissionLevel ?? 'none',
      refereeChecksRequired: data?.vm.task?.refereeChecksRequired ?? (type === 'referee_check' ? 2 : undefined),
      refereeInstructions: data?.vm.task?.refereeInstructions ?? '',
      stripeProductId: data?.vm.task?.stripeProductId ?? '',
      autoCompleteOnPayment: data?.vm.task?.autoCompleteOnPayment ?? true,
      allowSelfBooking: data?.vm.task?.eventConfig?.allowSelfBooking ?? false,
      bookingRecurrence: null,
      availabilitySchedule: data?.vm.task?.eventConfig?.availabilitySchedule
        ? JSON.parse(data.vm.task.eventConfig.availabilitySchedule)
        : null,
      defaultSlotDuration: data?.vm.task?.eventConfig?.defaultSlotDuration ?? null,
      defaultMaxCapacity: data?.vm.task?.eventConfig?.defaultMaxCapacity ?? 1,
      defaultRemoteLocation: data?.vm.task?.eventConfig?.defaultRemoteLocation ?? null,
      defaultMessage: data?.vm.task?.eventConfig?.defaultMessage ?? null,
      defaultTimezone: data?.vm.task?.eventConfig?.defaultTimezone ?? null,
      defaultManagerIds: data?.vm?.task?.defaultManagers?.map((m: any) => m.managerId) ?? null,
      defaultManagers: data?.vm?.task?.defaultManagers ?? null,
      hasBadge: data?.vm.task?.hasBadge ?? false,
      badgeIcon: data?.vm.task?.badgeIcon ?? null,
      badgeMode: data?.vm.task?.badgeMode ?? null,
      badgeFieldId: data?.vm.task?.badgeFieldId ?? null,
      badgeFieldValue: data?.vm.task?.badgeFieldValue ?? null,
    };
  }, [data, tid, duplicate, cakeSelectItems, programOptions, roleOptions, type, cid, cakesData, getNextOrderForCake]);

  const isTaskZero = useMemo(() => data?.vm.task?.order === -1, [data]);

  const parseRecurrence = (values: TaskFormType): Recurrence | undefined => {
    const startDate = values.startDate ? DateTime.fromJSDate(values.startDate) : DateTime.local();

    const baseRecurrence = {
      startDate: encodeDate(startDate),
      endDate: null,
      humanReadable: '', // This is a dummy so we can reuse autogenerated type
    };
    if (!values.frequency) return undefined;

    if (values.frequency === 'monthly') {
      const monthDay = startDate.day;
      return {
        ...baseRecurrence,
        __typename: 'MonthlyByMonthDayRecurrenceType',
        monthDay,
        monthInterval: 1,
      };
    }
    if (values.frequency === 'quarterly') {
      const monthDay = startDate.day;
      return {
        ...baseRecurrence,
        __typename: 'MonthlyByMonthDayRecurrenceType',
        monthDay,
        monthInterval: 3,
      };
    }
    if (values.frequency === 'half_annually') {
      const monthDay = startDate.day;
      return {
        ...baseRecurrence,
        __typename: 'MonthlyByMonthDayRecurrenceType',
        monthDay,
        monthInterval: 6,
      };
    }
    if (values.frequency === 'annually') {
      const monthDay = startDate.day;
      return {
        ...baseRecurrence,
        __typename: 'MonthlyByMonthDayRecurrenceType',
        monthDay,
        monthInterval: 12,
      };
    }
    return assertNever(values.frequency);
  };

  async function handleSave(values: TaskFormType) {
    const orderedTaskItems = values.taskItems.allIds
      .filter((itemId) => {
        // For cross-task dynamic fields, we need special handling
        const triggerOption = values.taskItems.byTriggerOption[itemId];
        if (triggerOption) {
          // This is a triggered item
          const trigger = values.taskItems.byTrigger[triggerOption];
          if (trigger && trigger.optionType === 'crossTaskDynamic') {
            // For cross-task dynamic, triggered items need to exist both inside triggers AND as top-level items
            // Include them so they don't get soft-deleted by the backend
            return !itemId.startsWith('new');
          }
          // For regular dynamic fields, include triggered items if they're not new
          return !itemId.startsWith('new');
        }
        // Include all non-triggered items
        return true;
      })
      .map((itemId) => createTaskItem(itemId, values.taskItems, !!duplicate))
      .map((item, taskItemIndex) => set(item, 'order', taskItemIndex));
    const expiryRecurrence = values.frequency ? parseRecurrence(values) : undefined;
    const valuesForSubmission = { ...values, expiryRecurrences: expiryRecurrence ? [expiryRecurrence] : undefined };
    if (values.programIds?.includes('all')) valuesForSubmission.programIds = [];
    if (!valuesForSubmission.programIds || valuesForSubmission.programIds.length === 0)
      valuesForSubmission.activityIds = [];
    if (values.permissionLevel === 'none') valuesForSubmission.permissionLevel = undefined;

    // Include the task type if it's an event, referee check, or stripe product task
    const taskData: any = { ...valuesForSubmission, taskItems: orderedTaskItems };
    if (type === 'event' || type === 'referee_check' || type === 'stripe_product') {
      taskData.taskType = type;
    }
    if (type === 'event') {
      taskData.allowSelfBooking = values.allowSelfBooking ?? false;
      if (values.allowSelfBooking) {
        taskData.bookingRecurrences = values.bookingRecurrence ? [values.bookingRecurrence] : undefined;
        taskData.availabilitySchedule = values.availabilitySchedule ?? undefined;
        taskData.defaultSlotDuration = values.defaultSlotDuration ?? undefined;
        taskData.defaultMaxCapacity = values.defaultMaxCapacity ?? undefined;
        taskData.defaultRemoteLocation = values.defaultRemoteLocation ?? undefined;
        taskData.defaultMessage = values.defaultMessage ?? undefined;
        taskData.defaultTimezone = values.defaultTimezone ?? undefined;
        taskData.defaultManagerIds = values.defaultManagerIds ?? undefined;
      }
      delete taskData.defaultPlacesAddress;
      delete taskData.defaultManagers;
    }

    runSaveTask(taskData, () => {
      if (refetch)
        refetch().then((res) => {
          if (res.data) history.goBack();
        });
    });
  }

  return (
    <FormBuilderDialogProvider>
      <Formik enableReinitialize initialValues={initValues} validationSchema={validationSchema} onSubmit={handleSave}>
        {({ values }) => {
          const activityOptions =
            getAllProgramsData?.vm.programs
              .filter((program) => values.programIds?.includes(program.programId))
              .flatMap((program) =>
                program.activities.map(({ activityId, name }) => ({
                  programId: program.programId,
                  activityId,
                  name,
                }))
              ) || [];

          return (
            <Form style={{ width: '100%', backgroundColor: theme.palette.grey[100] }}>
              <AdminConsoleVolunteerProfileHeader>
                <FormBuilderHeader
                  taskName={values.title}
                  taskId={values.taskId}
                  isTaskZero={values.order === -1}
                  isManagerTask={values.managerCompleted}
                  disabled={isLoading}
                  isRefereeCheckTask={type === 'referee_check'}
                  isEventTask={type === 'event'}
                  isPaymentTask={type === 'stripe_product'}
                />
              </AdminConsoleVolunteerProfileHeader>

              <Grid container justify='center'>
                <Grid item xs={10} sm={6}>
                  <TaskDetailsCard
                    isNew={!tid}
                    cakeSelectItems={cakeSelectItems}
                    taskStageSelectItems={taskStageSelectItems}
                    remainingManualTasks={remainingManualTasks}
                    isTaskZero={values.order === -1}
                    programOptions={programOptions}
                    activityOptions={activityOptions}
                    roleOptions={roleOptions}
                    permissionLevelOptions={permissionLevelOptions}
                    onTaskStageAdd={handleTaskStageAdd}
                    disabled={isLoading}
                    taskType={type}
                    bookableTimeSlots={data?.vm?.task?.bookableTimeSlots}
                    availableManagers={(data as any)?.vm?.admins ?? []}
                    onRefetch={() => (tid ? getTask({ variables: { taskId: tid } }) : undefined)}
                  />
                  {isMobile && <TaskControlsContent taskType={type} />}
                  {isTaskZero ? (
                    <List style={{ padding: 0, marginTop: 8 }}>
                      {Object.keys(volunteerProfileHardCodedFields).map((key) => (
                        <ListItem style={{ padding: '8px 0px' }} key={volunteerProfileHardCodedFields[key].name}>
                          <HardCodedFieldTaskItem
                            __typename={volunteerProfileHardCodedFields[key].__typename}
                            name={volunteerProfileHardCodedFields[key].name}
                            description={volunteerProfileHardCodedFields[key].description}
                            optional={volunteerProfileHardCodedFields[key].optional}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : null}
                  {type === 'referee_check' ? (
                    <List style={{ padding: 0, marginTop: 8 }}>
                      {Object.keys(refereeCheckHardCodedFields).map((key) => (
                        <ListItem style={{ padding: '8px 0px' }} key={refereeCheckHardCodedFields[key].name}>
                          <HardCodedFieldTaskItem
                            __typename={refereeCheckHardCodedFields[key].__typename}
                            name={refereeCheckHardCodedFields[key].name}
                            description={refereeCheckHardCodedFields[key].description}
                            optional={refereeCheckHardCodedFields[key].optional}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : null}
                  <TaskListItemsForm programs={programOptions} isTaskZero={values.order === -1} />
                </Grid>

                {!isMobile && (
                  <Grid item sm={3} style={{ paddingLeft: '16px' }}>
                    <TaskControlsCard taskType={type} />
                  </Grid>
                )}
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </FormBuilderDialogProvider>
  );
});
